/**
 * Decides what the practitioner's two surfaces show: the main area and the client-preview
 * footer. Kept in one place because PractitionerPage and ControlPanelFooter have to agree
 * exactly — each of them renders one half of the same layout.
 */
export interface PractitionerViewLayout {
  mainHasCamera: boolean;
  mainHasMedia: boolean;
  mainHasGame: boolean;
  footerHasCamera: boolean;
  footerHasMedia: boolean;
  footerHasGame: boolean;
}

interface PractitionerViewLayoutInput {
  isCallActive: boolean;
  /** Media is loaded and saved — not necessarily visible to the client yet. */
  hasMedia: boolean;
  viewSwapped: boolean;
}

export const getPractitionerViewLayout = ({
  isCallActive,
  hasMedia,
  viewSwapped,
}: PractitionerViewLayoutInput): PractitionerViewLayout => {
  if (!isCallActive) {
    // No call means no client video, so there is nothing for the client view to trade
    // places with. This keeps the long-standing pre-call layout untouched: media takes the
    // main area, the task stays in the footer, and the swap moves them past each other.
    const gameInMain = viewSwapped && hasMedia;
    return {
      mainHasCamera: false,
      mainHasMedia: hasMedia && !viewSwapped,
      mainHasGame: gameInMain,
      footerHasCamera: false,
      footerHasMedia: hasMedia && viewSwapped,
      footerHasGame: !gameInMain,
    };
  }

  // During a call the task and the media travel together as one unit: both are what the
  // client is looking at (the media covers the task while it is being shown), so putting
  // them on different surfaces is what made the "Client view" panel misrepresent the
  // client. The call video is the only content the client never sees, so that is the one
  // thing the swap exchanges the client view with.
  const clientViewInMain = viewSwapped;
  return {
    mainHasCamera: !clientViewInMain,
    mainHasMedia: hasMedia && clientViewInMain,
    mainHasGame: clientViewInMain,
    footerHasCamera: clientViewInMain,
    footerHasMedia: hasMedia && !clientViewInMain,
    footerHasGame: !clientViewInMain,
  };
};
